import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import GenericFeatureList from '../shared/generic-feature-list';
import { DailyReportAPI } from '../../services/feature-api';
import { getFeatureConfig } from '../../config/featureDisplayConfig';

const config = getFeatureConfig('daily-report');
const accent = config?.color || '#2563eb';

// Parse checklist for completion stats
const parseChecklist = (raw) => {
  if (!raw) return [];
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    } catch {
      return raw.split('\n').filter(Boolean).map((t) => ({ text: t, done: false }));
    }
  }
  return Array.isArray(raw) ? raw : [];
};

const fetchData = async (accessToken, filters) => {
  const result = await DailyReportAPI.getList(accessToken, filters);
  const rawItems = result.success ? (result.data?.rows || result.data?.items || []) : [];
  const list = Array.isArray(rawItems) ? rawItems : [];

  const enriched = list.map((item) => {
    const tasks = parseChecklist(item.checklist);
    const total = tasks.length;
    const done = tasks.filter((t) => t.done).length;
    const pct = total > 0 ? Math.round((done / total) * 100) : 0;
    return {
      ...item,
      _completionText: total > 0 ? `${done}/${total}` : '—',
      _completionPct: pct,
    };
  });

  return { items: enriched, summary: { total: enriched.length } };
};

const getColumns = () => {
  const cols = [...(config.listConfig?.columns || [])];
  if (!cols.some((c) => c.key === '_completionText')) {
    cols.push({
      key: '_completionText',
      label: 'Hoàn thành',
      visible: true,
      format: 'text',
      render: (val, row) => {
        const pct = row._completionPct || 0;
        const color = pct === 100 ? '#22c55e' : pct >= 50 ? '#f59e0b' : '#ef4444';
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ flex: 1, height: '6px', background: '#f1f5f9', borderRadius: '3px', minWidth: '40px', overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: '3px', transition: 'width 0.3s' }} />
            </div>
            <span style={{ fontSize: '11px', fontWeight: 600, color, whiteSpace: 'nowrap' }}>
              {val} ({pct}%)
            </span>
          </div>
        );
      },
    });
  }
  return cols;
};

const DailyReportList = (props) => {
  const navigate = useNavigate();
  const today = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  }, []);

  // Smart action button: checks if today's report already exists
  const renderActionButton = (items) => {
    const todayReport = items.find((item) => item.date === today);
    const isToday = todayReport != null;

    return (
      <button
        onClick={() => {
          if (isToday) {
            navigate(config.editPath, { state: { editData: todayReport } });
          } else {
            navigate(config.createPath);
          }
        }}
        className="w-full flex items-center justify-center gap-3 py-4 rounded-2xl text-white font-bold text-base shadow-lg hover:shadow-xl active:scale-[.97] transition-all"
        style={{
          background: isToday
            ? `linear-gradient(135deg, ${accent}, ${accent}cc)`
            : `linear-gradient(135deg, #22c55e, #16a34a)`,
        }}
      >
        <span style={{ fontSize: '20px' }}>{isToday ? '📋' : '➕'}</span>
        <span>{isToday ? 'Cập nhật Mục tiêu CV hôm nay' : 'Tạo báo cáo hôm nay'}</span>
      </button>
    );
  };

  // Total row computed from items
  const computeTotalRow = (items) => {
    const totalTasks = items.reduce((sum, item) => {
      const tasks = parseChecklist(item.checklist);
      return sum + tasks.length;
    }, 0);
    const doneTasks = items.reduce((sum, item) => {
      const tasks = parseChecklist(item.checklist);
      return sum + tasks.filter(t => t.done).length;
    }, 0);
    const avgPct = totalTasks > 0 ? Math.round((doneTasks / totalTasks) * 100) : 0;
    return {
      label: `📊 Tổng: ${items.length} báo cáo`,
      values: {
        tasks: `${doneTasks}/${totalTasks} việc`,
        pct: `${avgPct}% hoàn thành`,
      },
    };
  };

  return (
    <GenericFeatureList
      title={config.listConfig.title}
      fetchData={fetchData}
      columns={getColumns()}
      summaryFields={config.listConfig.summaryFields}
      displayMode={config.listConfig.displayMode}
      notice={config.notice}
      guideUrl={config.guideUrl}
      accentColor={config.color}
      onItemClick={(item) => navigate(config.editPath, { state: { editData: item } })}
      actionButton={renderActionButton}
      totalRow={(items) => computeTotalRow(items)}
      {...props}
    />
  );
};

export default DailyReportList;
